import { useQueryClient } from '@tanstack/react-query';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  Role,
  apiInstance,
  curityApiInstance,
  storageInstance,
} from '../services';
import {
  Credentials,
  SerializedCredentials,
  deserializeCredentials,
  isCredentialsExpired,
} from '../services/service-trips-api/transformers/credentials';
import { OAuthError, isTransientRefreshError } from '../services/service-trips-api/types/curity';
import type { IndexPermissions } from '../services/service-trips-api/types/user-v2';
import {
  currentEnvAppConfig,
  generateCurityLogoutUrl,
  generateCurityUrl,
  generateRandomString,
  logger,
} from '../utils';
import { IdTokenIdentity, decodeIdToken, decodeIdTokenPayload } from '../utils/jwt/decode';
import { useLocale } from './LocaleContext';
import type { AuthContextType, AuthProviderProps, CurityConfig } from './types';

const { curityRedirectUri, curityClientId, curityOauthBaseUrl } =
  currentEnvAppConfig;

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

// ─── Module-level pure helpers ───────────────────────────────────────────────

function hasPermissionsKey(data: unknown): data is Record<'permissions', unknown> {
  return typeof data === 'object' && data !== null && 'permissions' in data;
}

function isIndexPermissions(value: unknown): value is IndexPermissions {
  if (typeof value !== 'object' || value === null) return false;
  const requiredKeys: (keyof IndexPermissions)[] = [
    'canSeeTripsNavItem',
    'canSearchTrips',
    'canSeePassengersNavItem',
    'canSearchPassengers',
    'canSeeUsersNavItem',
    'canSearchUsers',
    'canSeeGeneralAbsenceMenuItem',
    'canSeeNotificationsMenuItem',
  ];
  return requiredKeys.every(
    (key) =>
      key in value &&
      // key is restricted to the fixed IndexPermissions key list above, not user input
      // eslint-disable-next-line security/detect-object-injection
      typeof (value as Record<string, unknown>)[key] === 'boolean'
  );
}

async function clearOAuthStorage(): Promise<void> {
  await storageInstance.setEphemeralData('oauth_state', null);
  await storageInstance.setEphemeralData('code_verifier', null);
}

async function clearAllOAuthEphemeralKeys(): Promise<void> {
  await clearOAuthStorage();
  await storageInstance.setEphemeralData('oauth_nonce', null);
}

async function validateOAuthState(state?: string): Promise<string> {
  const codeVerifier = await storageInstance.getEphemeralData<string>('code_verifier');
  if (!codeVerifier) {
    await clearAllOAuthEphemeralKeys();
    throw new Error('No code verifier in storage');
  }
  const storedState = await storageInstance.getEphemeralData<string>('oauth_state');
  if (!storedState || !state) {
    await clearAllOAuthEphemeralKeys();
    throw new Error('OAuth state missing — possible CSRF attack');
  }
  if (storedState !== state) {
    await clearAllOAuthEphemeralKeys();
    throw new Error('OAuth state mismatch — possible CSRF attack');
  }
  await clearOAuthStorage();
  return codeVerifier;
}

async function resolveCredentials(
  credentials: SerializedCredentials,
): Promise<string | undefined> {
  const deserialized = deserializeCredentials(credentials);
  // Populate the singleton unconditionally, before the expiry check. Previously this only
  // ran in the non-expired branch, so a cold start with an expired token called
  // refreshToken() while `apiInstance.credentials` was still unset — handleRefreshSuccess's
  // guard then discarded the (successful) refresh, leaving the stale expired token in
  // storage and logging the user out on their very next request (ENB-2472).
  apiInstance.setCredentials(deserialized);
  if (isCredentialsExpired(deserialized)) {
    await apiInstance.refreshToken(deserialized);
    // After refresh, re-read credentials from storage since refreshToken writes new ones
    const refreshed = await storageInstance.getData<SerializedCredentials>('credentials');
    const refreshedCreds = refreshed ? deserializeCredentials(refreshed) : null;
    // Curity may omit id_token from refresh responses (only required on code exchange).
    // Fall back to the pre-refresh id_token — it identifies the same user and is safe
    // to keep for display purposes (navbar, feedback URL submitter field, etc.).
    return refreshedCreds?.idToken ?? deserialized.idToken;
  }
  return deserialized.idToken;
}

const PROFILE_STORAGE_KEY = 'user_profile' as const;

type StoredUserProfile = {
  roles?: string[];
  id?: string;
  lastLogin?: string | null;
};

// ─── Sub-hooks ───────────────────────────────────────────────────────────────

const useIdentityState = () => {
  const [identity, setIdentity] = useState<IdTokenIdentity | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [lastLogin, setLastLogin] = useState<string | null | undefined>();
  const applyIdentityFromToken = useCallback((idToken: string | undefined): void => {
    // Prevent last-login metadata from leaking across identity changes.
    setLastLogin(undefined);
    if (!idToken) {
      setIdentity(null);
      setRoles([]);
      return;
    }
    try {
      const decoded = decodeIdToken(idToken);
      setIdentity(decoded);
      // V2: sub claim is the user's UUID — expose for user-scoped V2 APIs
      apiInstance.setAuthUserUuid(decoded.sub);
      // Set numeric authUserId from token for trips/passengers endpoints
      if (decoded.providerUserId != null) {
        apiInstance.setAuthUserId(decoded.providerUserId);
      }
    } catch (e) {
      logger.error('Failed to decode id_token', e);
      setIdentity(null);
    }
  }, []);

  const fetchProfile = useCallback(async (): Promise<void> => {
    try {
      const profile = await apiInstance.getProfile();
      const profileRoles = profile?.roles ?? [];
      const profileId = profile?.id;
      const profileLastLogin = profile?.lastLogin;
      setRoles(profileRoles);
      setLastLogin(profileLastLogin);
      if (profileId) {
        apiInstance.setAuthUserUuid(profileId);
      }
      await storageInstance.setData(PROFILE_STORAGE_KEY, {
        roles: profileRoles,
        id: profileId,
        lastLogin: profileLastLogin,
      });
    } catch (e) {
      logger.error('Failed to fetch profile', e);
      // Don't block auth flow — cached roles may still be available.
    }
  }, []);

  const loadProfileFromStorage = useCallback(async (): Promise<boolean> => {
    try {
      const stored = await storageInstance.getData<StoredUserProfile>(PROFILE_STORAGE_KEY);
      if (!stored) return false;
      setRoles(stored.roles ?? []);
      if (stored.id) {
        apiInstance.setAuthUserUuid(stored.id);
      }
      const hasLastLogin = Object.prototype.hasOwnProperty.call(stored, 'lastLogin');
      setLastLogin(hasLastLogin ? stored.lastLogin : undefined);
      return hasLastLogin;
    } catch (e) {
      logger.error('Failed to load profile from storage', e);
      return false;
    }
  }, []);

  return { identity, roles, lastLogin, setIdentity, setRoles, setLastLogin, applyIdentityFromToken, fetchProfile, loadProfileFromStorage };
};

const usePermittedLinks = () => {
  const [indexPermissions, setIndexPermissions] = useState<IndexPermissions | undefined>();
  const getAuthPermissions = useCallback(async () => {
    try {
      const data = await apiInstance.getAuthPermissions();
      if (hasPermissionsKey(data) && isIndexPermissions(data.permissions)) {
        setIndexPermissions(data.permissions);
      }
    } catch (e) {
      logger.error('Failed to get permissions', e);
      throw e;
    }
  }, []);
  return { indexPermissions, getAuthPermissions };
};

const useGenerateLoginUri = (curityConfig: CurityConfig, currentLanguage: string) =>
  useCallback(async (
    encrypt: (data: string) => Promise<string>,
    redirectUri?: string,
  ): Promise<string> => {
    const codeVerifier = generateRandomString(64);
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);
    const codeChallenge = await encrypt(codeVerifier);
    await storageInstance.setEphemeralData('code_verifier', codeVerifier);
    await storageInstance.setEphemeralData('oauth_state', state);
    await storageInstance.setEphemeralData('oauth_nonce', nonce);
    return generateCurityUrl({
      redirectUri: redirectUri || curityConfig.redirectUri,
      codeChallenge,
      clientId: curityConfig.clientId,
      oauthBaseUrl: curityConfig.oAuthBaseUrl,
      uiLocale: currentLanguage,
      state,
      nonce,
    });
  }, [curityConfig.redirectUri, curityConfig.clientId, curityConfig.oAuthBaseUrl, currentLanguage]);

const useVerifyCode = (
  applyIdentityFromToken: (idToken: string | undefined) => void,
  getAuthPermissions: () => Promise<void>,
  fetchProfile: () => Promise<void>,
  setIsAuthenticated: (v: boolean) => void,
) =>
  useCallback(async (
    code: string,
    redirectUri?: string,
    state?: string,
  ): Promise<void> => {
    const codeVerifier = await validateOAuthState(state);
    const credentials = await curityApiInstance.authorizeCode({ code, codeVerifier, redirectUri });

    // Verify nonce in id_token to prevent replay attacks (fail-closed)
    const storedNonce = await storageInstance.getEphemeralData<string>('oauth_nonce');
    try {
      if (storedNonce) {
        if (!credentials.idToken) {
          throw new Error('ID token missing but nonce was expected');
        }
        const payload = decodeIdTokenPayload(credentials.idToken);
        if (!payload.nonce || payload.nonce !== storedNonce) {
          throw new Error('ID token nonce mismatch — possible replay attack');
        }
      }
    } finally {
      await storageInstance.setEphemeralData('oauth_nonce', null);
    }

    await storageInstance.setData('credentials', credentials);
    // A fresh login is a new session: reset the transient-failure counter (so a run of
    // outage-era failures can't latch across a re-login) and bump the epoch (so a refresh
    // still in flight for the previous session can't apply its result here). Deliberately
    // *after* the exchange succeeds — AuthCallback renders behind RouterLoader, so a live
    // session already exists in memory and must survive a failed/replayed callback.
    apiInstance.clearSession();
    apiInstance.setCredentials(credentials);
    applyIdentityFromToken(credentials.idToken);
    await getAuthPermissions();
    await fetchProfile();
    setIsAuthenticated(true);
  }, [applyIdentityFromToken, getAuthPermissions, fetchProfile, setIsAuthenticated]);

const useLogout = (
  curityConfig: CurityConfig,
  handleLogout: (url: string) => void,
  logoutRedirectUri: string | undefined,
  queryClient: ReturnType<typeof useQueryClient>,
  setIdentity: (i: IdTokenIdentity | null) => void,
  setRoles: (r: string[]) => void,
  setLastLogin: (value: string | null | undefined) => void,
  setIsAuthenticated: (v: boolean) => void,
) =>
  useCallback(async (silent = false) => {
    const credentials = await storageInstance.getData<Credentials>('credentials');
    setIdentity(null);
    setRoles([]);
    setLastLogin(undefined);
    setIsAuthenticated(false);
    queryClient.clear();
    await storageInstance.setEphemeralData('code_verifier', null);
    await storageInstance.setEphemeralData('oauth_state', null);
    await storageInstance.setEphemeralData('oauth_nonce', null);
    await storageInstance.setData('credentials', null);
    await storageInstance.setData(PROFILE_STORAGE_KEY, null);
    apiInstance.clearSession();
    if (!silent) {
      if (credentials?.idToken) {
        handleLogout(generateCurityLogoutUrl({
          clientId: curityConfig.clientId,
          oauthBaseUrl: curityConfig.oAuthBaseUrl,
          idToken: credentials.idToken,
          redirectUri: logoutRedirectUri,
        }));
      } else if (logoutRedirectUri) {
        handleLogout(logoutRedirectUri);
      }
    }
  }, [curityConfig.clientId, curityConfig.oAuthBaseUrl, handleLogout, logoutRedirectUri, queryClient, setIdentity, setRoles, setLastLogin, setIsAuthenticated]);

const useRefreshUser = (
  logout: (silent?: boolean) => Promise<void>,
  applyIdentityFromToken: (idToken: string | undefined) => void,
  getAuthPermissions: () => Promise<void>,
  loadProfile: () => Promise<boolean>,
  fetchProfile: () => Promise<void>,
  setIsAuthenticated: (v: boolean) => void,
) =>
  useCallback(async () => {
    try {
      const credentials = await storageInstance.getData<SerializedCredentials>('credentials');
      if (!credentials) {
        apiInstance.setUnAuthorizedObserver(logout);
        return;
      }
      applyIdentityFromToken(await resolveCredentials(credentials));
      await getAuthPermissions();
      const loaded = await loadProfile();
      if (!loaded) await fetchProfile();
      setIsAuthenticated(true);
      apiInstance.setUnAuthorizedObserver(logout);
    } catch (e) {
      // TypeError/TimeoutError are pre-existing transient cases (network blip, storage
      // read error). `OAuthError` is new (ENB-2472): reuses ServiceTripsApi's per-request
      // refresh classifier so a startup refresh hitting e.g. a Curity 503 retries the same
      // way a mid-session refresh would, instead of silently logging out on the first
      // strike — but only for OAuthError. Other startup failures (storage, permissions/
      // profile fetch) keep their existing behavior: anything unrecognized is terminal.
      const isTransient =
        e instanceof TypeError ||
        (e instanceof DOMException && e.name === 'TimeoutError') ||
        (e instanceof OAuthError && isTransientRefreshError(e));
      if (!isTransient) {
        // Silent logout: stale/invalid credentials during startup — clear the
        // session without opening the browser's logout page, since the user
        // never explicitly logged out and the Curity session is already invalid.
        await logout(true);
        return;
      }
      // Transient failure (network blip, storage read error) — bubble up so the
      // caller can schedule a retry.
      throw e;
    }
  }, [logout, applyIdentityFromToken, getAuthPermissions, loadProfile, fetchProfile, setIsAuthenticated]);



// ─── Hook ────────────────────────────────────────────────────────────────────

const useAuthProvider = (
  handleLogout: (url: string) => void,
  logoutRedirectUri?: string,
): AuthContextType => {
  const { identity, roles, lastLogin, setIdentity, setRoles, setLastLogin, applyIdentityFromToken, fetchProfile, loadProfileFromStorage } = useIdentityState();
  const { indexPermissions, getAuthPermissions } = usePermittedLinks();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [curityConfig, setCurityConfig] = useState<CurityConfig>({
    clientId: curityClientId,
    redirectUri: curityRedirectUri,
    oAuthBaseUrl: curityOauthBaseUrl,
  });
  const queryClient = useQueryClient();
  const { currentLanguage } = useLocale();

  const generateLoginUri = useGenerateLoginUri(curityConfig, currentLanguage);
  const verifyCode = useVerifyCode(applyIdentityFromToken, getAuthPermissions, fetchProfile, setIsAuthenticated);
  const logout = useLogout(curityConfig, handleLogout, logoutRedirectUri, queryClient, setIdentity, setRoles, setLastLogin, setIsAuthenticated);
  const refreshUser = useRefreshUser(logout, applyIdentityFromToken, getAuthPermissions, loadProfileFromStorage, fetchProfile, setIsAuthenticated);
  const isSuperUser = useMemo(() => roles.includes(Role.SuperUser), [roles]);

  return useMemo(
    () => ({ logout, verifyCode, refreshUser, generateLoginUri, setCurityConfig, isAuthenticated, identity, roles, lastLogin, indexPermissions, isSuperUser }),
    [logout, verifyCode, refreshUser, generateLoginUri, setCurityConfig, isAuthenticated, identity, roles, lastLogin, indexPermissions, isSuperUser],
  );
};

// ─── Provider ────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<PropsWithChildren<AuthProviderProps>> = ({
  children,
  handleLogout,
  logoutRedirectUri,
}) => {
  const contextValue = useAuthProvider(handleLogout, logoutRedirectUri);
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => useContext(AuthContext);
